import type { PaginationChangedEvent, SortChangedEvent } from "ag-grid-community";
import { useCallback } from "react";

import { useStructuredSearchStore } from "@/components/StructuredSearch";

interface StructuredSearchGridPaginationSortOptions {
  onPageSizeChange?: (newPageSize: number, previousPageSize: number) => void;
  onPageIndexChange?: (newPageIndex: number, previousPageIndex: number) => void;
  onSortChange?: (sortField: string | null, sortDirection: "asc" | "desc" | null) => void;
}

export const useStructuredSearchGridPaginationSort = ({
  onPageSizeChange,
  onPageIndexChange,
  onSortChange,
}: StructuredSearchGridPaginationSortOptions = {}) => {
  const structuredSearchStore = useStructuredSearchStore();

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent) => {
      if (!event.newPage && !event.newPageSize) {
        return;
      }

      const newPageSize = event.api.paginationGetPageSize();
      const newCurrentPage = event.api.paginationGetCurrentPage() + 1;

      if (newPageSize !== structuredSearchStore.pageSize) {
        onPageSizeChange?.(newPageSize, structuredSearchStore.pageSize);
        structuredSearchStore.setPageSize(newPageSize);
        return;
      }

      if (newCurrentPage !== structuredSearchStore.pageIndex) {
        onPageIndexChange?.(newCurrentPage, structuredSearchStore.pageIndex);
        structuredSearchStore.setPageIndex(newCurrentPage);
      }
    },
    [onPageIndexChange, onPageSizeChange, structuredSearchStore],
  );

  const handleSortChanged = useCallback(
    <TRow>(event: SortChangedEvent<TRow>) => {
      const sortedColumn = event.api.getColumnState().find(column => column.sort !== null);
      if (!sortedColumn?.colId) {
        onSortChange?.(null, null);
        structuredSearchStore.setSorting([]);
        structuredSearchStore.resetToFirstPage();
        return;
      }

      const sortField = event.api.getColumn(sortedColumn.colId)?.getColDef().field;
      if (!sortField || typeof sortField !== "string") {
        onSortChange?.(null, null);
        structuredSearchStore.setSorting([]);
        structuredSearchStore.resetToFirstPage();
        return;
      }

      onSortChange?.(sortField, sortedColumn.sort ?? null);
      structuredSearchStore.setSorting([{ id: sortField, desc: sortedColumn.sort === "desc" }]);
      structuredSearchStore.resetToFirstPage();
    },
    [onSortChange, structuredSearchStore],
  );

  return {
    currentPage: structuredSearchStore.pageIndex,
    pageSize: structuredSearchStore.pageSize,
    handlePaginationChanged,
    handleSortChanged,
  };
};
